import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { getStoredLocale } from './lib/localeStorage'
import LangLayout from './components/layout/LangLayout'
import DashboardPage from './pages/DashboardPage'
import InboxPage from './pages/InboxPage'
import BookingsPage from './pages/BookingsPage'
import CustomersPage from './pages/CustomersPage'
import FollowersPage from './pages/FollowersPage'
import TagsPage from './pages/TagsPage'
import CustomStatusPage from './pages/CustomStatusPage'
import SettingsPage from './pages/SettingsPage'
import AdminPermissionsPage from './pages/AdminPermissionsPage'
import BotConfigPage from './pages/BotConfigPage'
import FaqPage from './pages/FaqPage'
import PlaybookPage from './pages/PlaybookPage'
import QuickAnswerPage from './pages/QuickAnswerPage'
import ReplyLogPage from './pages/ReplyLogPage'
import BillingPage from './pages/BillingPage'
import ProductsPage from './pages/ProductsPage'
import ProductFormPage from './pages/ProductFormPage'
import OrdersPage from './pages/OrdersPage'
import OrderDetailPage from './pages/OrderDetailPage'
import OrderFormPage from './pages/OrderFormPage'
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import VerifyOtpPage from './pages/VerifyOtpPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import OnboardingPage from './pages/OnboardingPage'
import AutomationsPage from './pages/AutomationsPage'
import PaymentSuccessPage from './pages/PaymentSuccessPage'
import AcceptInvitePage from './pages/AcceptInvitePage'
import FacebookConnectedPage from './pages/FacebookConnectedPage'
import NotificationsPage from './pages/NotificationsPage'
import useManagerStore from './store/managerStore'
import { canAccess, normalizeRole, RBAC_MODULES, TENANT_FEATURE_FLAGS } from './lib/rbac'
import { useFeatureFlag } from './lib/useFeatureFlag'

function LegacyLangRedirect({ suffix }) {
  const { search, hash } = useLocation()
  const loc = getStoredLocale()
  return <Navigate to={`/${loc}${suffix}${search}${hash}`} replace />
}

// Notification links name a contact as `/contacts/:id`; the manager shows a
// contact as the detail panel of the customers page. Being a route (rather
// than a rewrite at the click site) also covers links opened from a push
// notification and notifications stored before this existed.
function NotificationContactRedirect() {
  const { contactId } = useParams()
  const loc = getStoredLocale()
  return <Navigate to={`/${loc}/customers?contact=${encodeURIComponent(contactId)}`} replace />
}

function LegacyProductEditRedirect() {
  const { productId } = useParams()
  const { search, hash } = useLocation()
  const loc = getStoredLocale()
  return <Navigate to={`/${loc}/products/${productId}/edit${search}${hash}`} replace />
}

function LegacyOrderDetailRedirect() {
  const { orderId } = useParams()
  const { search, hash } = useLocation()
  const loc = getStoredLocale()
  return <Navigate to={`/${loc}/orders/${orderId}${search}${hash}`} replace />
}

function RequireModuleAccess({ moduleName, minimumLevel, children }) {
  const { lang } = useParams()
  const storeRole = useManagerStore((s) => s.role)
  const resolvedRole = normalizeRole(storeRole)
  const resolvedLang = lang === 'en' || lang === 'vi' ? lang : getStoredLocale()
  const canView = canAccess(resolvedRole, moduleName, minimumLevel)

  if (!canView) {
    return <Navigate to={`/${resolvedLang}/dashboard`} replace />
  }
  return children
}

// Tenant-level GrowthBook gate, checked in addition to RequireModuleAccess's
// per-role RBAC check. Default false (opt-in) — see TENANT_FEATURE_FLAGS.
function RequireFeatureFlag({ flagKey, children }) {
  const { lang } = useParams()
  const resolvedLang = lang === 'en' || lang === 'vi' ? lang : getStoredLocale()
  const enabled = useFeatureFlag(flagKey, false)

  if (!enabled) {
    return <Navigate to={`/${resolvedLang}/dashboard`} replace />
  }
  return children
}

export default function App() {
  const home = `/${getStoredLocale()}/dashboard`
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Navigate to={home} replace />} />
        <Route path="/public/payment-infor" element={<PaymentSuccessPage />} />
        <Route path="/payment/onepay/result" element={<PaymentSuccessPage />} />
        <Route path="/facebook-connected" element={<FacebookConnectedPage />} />
        <Route path="/contacts/:contactId" element={<NotificationContactRedirect />} />
        {/* Older journey notifications link to a conversation id, which the
            inbox cannot select by — they open the inbox itself. */}
        <Route path="/conversations/:conversationId" element={<LegacyLangRedirect suffix="/inbox" />} />
        <Route path="/notifications" element={<LegacyLangRedirect suffix="/notifications" />} />

        <Route path="/dashboard" element={<LegacyLangRedirect suffix="/dashboard" />} />
        <Route path="/inbox" element={<LegacyLangRedirect suffix="/inbox" />} />
        <Route path="/bookings" element={<LegacyLangRedirect suffix="/bookings" />} />
        <Route path="/customers" element={<LegacyLangRedirect suffix="/customers" />} />
        <Route path="/followers" element={<LegacyLangRedirect suffix="/followers" />} />
        <Route path="/tags" element={<LegacyLangRedirect suffix="/tags" />} />
        <Route path="/custom-statuses" element={<LegacyLangRedirect suffix="/custom-statuses" />} />
        <Route path="/products/new" element={<LegacyLangRedirect suffix="/products/new" />} />
        <Route path="/products/:productId/edit" element={<LegacyProductEditRedirect />} />
        <Route path="/products" element={<LegacyLangRedirect suffix="/products" />} />
        <Route path="/orders/new" element={<LegacyLangRedirect suffix="/orders/new" />} />
        <Route path="/orders/:orderId" element={<LegacyOrderDetailRedirect />} />
        <Route path="/orders" element={<LegacyLangRedirect suffix="/orders" />} />
        <Route path="/automations" element={<LegacyLangRedirect suffix="/automations" />} />
        <Route path="/settings" element={<LegacyLangRedirect suffix="/settings" />} />
        <Route path="/admin-permissions" element={<LegacyLangRedirect suffix="/admin-permissions" />} />
        <Route path="/bot-config" element={<LegacyLangRedirect suffix="/bot-config" />} />
        <Route path="/faq" element={<LegacyLangRedirect suffix="/faq" />} />
        <Route path="/playbooks" element={<LegacyLangRedirect suffix="/playbook" />} />
        <Route path="/billing" element={<LegacyLangRedirect suffix="/billing" />} />
        <Route path="/login" element={<LegacyLangRedirect suffix="/login" />} />
        <Route path="/signup" element={<LegacyLangRedirect suffix="/signup" />} />
        <Route path="/verify-otp" element={<LegacyLangRedirect suffix="/verify-otp" />} />
        <Route path="/forgot-password" element={<LegacyLangRedirect suffix="/forgot-password" />} />
        <Route path="/reset-password" element={<LegacyLangRedirect suffix="/reset-password" />} />
        <Route path="/onboarding" element={<LegacyLangRedirect suffix="/onboarding" />} />
        <Route path="/payment-success" element={<LegacyLangRedirect suffix="/payment-success" />} />
        <Route path="/accept-invite" element={<LegacyLangRedirect suffix="/accept-invite" />} />

        <Route path="/:lang" element={<LangLayout />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="login" element={<LoginPage />} />
          <Route path="signup" element={<SignupPage />} />
          <Route path="verify-otp" element={<VerifyOtpPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
          <Route path="onboarding" element={<OnboardingPage />} />
          <Route path="payment-success" element={<PaymentSuccessPage />} />
          <Route path="payment/onepay/result" element={<PaymentSuccessPage />} />
          <Route path="accept-invite" element={<AcceptInvitePage />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="inbox" element={<InboxPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route
            path="automations"
            element={(
              <RequireModuleAccess moduleName={RBAC_MODULES.automation}>
                <AutomationsPage />
              </RequireModuleAccess>
            )}
          />
          <Route
            path="bookings"
            element={(
              <RequireFeatureFlag flagKey={TENANT_FEATURE_FLAGS.bookings}>
                <BookingsPage />
              </RequireFeatureFlag>
            )}
          />
          <Route path="customers" element={<CustomersPage />} />
          <Route path="followers" element={<FollowersPage />} />
          <Route
            path="tags"
            element={(
              <RequireFeatureFlag flagKey={TENANT_FEATURE_FLAGS.contact_tags}>
                <RequireModuleAccess moduleName={RBAC_MODULES.contactTags}>
                  <TagsPage />
                </RequireModuleAccess>
              </RequireFeatureFlag>
            )}
          />
          <Route
            path="custom-statuses"
            element={(
              <RequireFeatureFlag flagKey={TENANT_FEATURE_FLAGS.custom_status}>
                <RequireModuleAccess moduleName={RBAC_MODULES.customStatus}>
                  <CustomStatusPage />
                </RequireModuleAccess>
              </RequireFeatureFlag>
            )}
          />
          <Route path="products" element={<ProductsPage />} />
          <Route path="products/new" element={<ProductFormPage />} />
          <Route path="products/:productId/edit" element={<ProductFormPage />} />
          <Route
            path="orders"
            element={(
              <RequireFeatureFlag flagKey={TENANT_FEATURE_FLAGS.orders}>
                <OrdersPage />
              </RequireFeatureFlag>
            )}
          />
          <Route
            path="orders/new"
            element={(
              <RequireFeatureFlag flagKey={TENANT_FEATURE_FLAGS.orders}>
                <OrderFormPage />
              </RequireFeatureFlag>
            )}
          />
          <Route
            path="orders/:orderId"
            element={(
              <RequireFeatureFlag flagKey={TENANT_FEATURE_FLAGS.orders}>
                <OrderDetailPage />
              </RequireFeatureFlag>
            )}
          />
          <Route path="settings" element={<SettingsPage />} />
          <Route
            path="admin-permissions"
            element={(
              <RequireModuleAccess moduleName={RBAC_MODULES.adminPermissions}>
                <AdminPermissionsPage />
              </RequireModuleAccess>
            )}
          />
          <Route
            path="bot-config"
            element={(
              <RequireModuleAccess moduleName={RBAC_MODULES.botConfig}>
                <BotConfigPage />
              </RequireModuleAccess>
            )}
          />
          <Route
            path="reply-log"
            element={(
              <RequireFeatureFlag flagKey={TENANT_FEATURE_FLAGS.bot_logs}>
                <RequireModuleAccess moduleName={RBAC_MODULES.botConfig}>
                  <ReplyLogPage />
                </RequireModuleAccess>
              </RequireFeatureFlag>
            )}
          />
          {/* Linked from the nav through navConfig (`faq`). */}
          <Route
            path="faq"
            element={(
              <RequireFeatureFlag flagKey={TENANT_FEATURE_FLAGS.faq}>
                <RequireModuleAccess moduleName={RBAC_MODULES.botConfig}>
                  <FaqPage />
                </RequireModuleAccess>
              </RequireFeatureFlag>
            )}
          />
          <Route
            path="quick-answers"
            element={(
              <RequireFeatureFlag flagKey={TENANT_FEATURE_FLAGS.quick_answer}>
                <RequireModuleAccess moduleName={RBAC_MODULES.botConfig}>
                  <QuickAnswerPage />
                </RequireModuleAccess>
              </RequireFeatureFlag>
            )}
          />
          <Route
            path="playbook"
            element={(
              <RequireFeatureFlag flagKey={TENANT_FEATURE_FLAGS.playbook}>
                <RequireModuleAccess moduleName={RBAC_MODULES.botConfig}>
                  <PlaybookPage />
                </RequireModuleAccess>
              </RequireFeatureFlag>
            )}
          />
          <Route
            path="billing"
            element={(
              <RequireModuleAccess moduleName={RBAC_MODULES.billing}>
                <BillingPage />
              </RequireModuleAccess>
            )}
          />
        </Route>

        <Route path="*" element={<Navigate to={home} replace />} />
      </Routes>
    </BrowserRouter>
  )
}
